import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Search, X, Loader2, Boxes, RefreshCw, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

const fmtIst = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(iso).slice(0, 16).replace('T', ' '); }
};

const StatusBadge = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const map = {
    open: { label: 'Open', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    closed: { label: 'Closed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    dispatched: { label: 'Dispatched', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  };
  const m = map[s] || { label: status || '—', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  return <Badge variant="outline" className={`border ${m.cls}`} data-testid={`mch-status-${s}`}>{m.label}</Badge>;
};

const SeasonalMasterCartonHistory = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_cartons: 0, total_boxes: 0, total_qty: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [printing, setPrinting] = useState('');
  const [canManage, setCanManage] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillApplying, setBackfillApplying] = useState(false);
  const pageSize = 50;

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const r = await api.getMasterCartons({
        q: opts.q ?? q, dateFrom: opts.dateFrom ?? dateFrom, dateTo: opts.dateTo ?? dateTo,
        page: opts.page ?? page, pageSize,
      });
      setRows(r.cartons || []);
      setSummary(r.summary || { total_cartons: 0, total_boxes: 0, total_qty: 0 });
      setTotal(r.total || 0);
      setCanManage(!!r.can_manage);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load master cartons');
    } finally { setLoading(false); }
  }, [q, dateFrom, dateTo, page]);
  useEffect(() => { load({ page: 1 }); /* initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openBackfill = async () => {
    setBackfillPreview(null);
    setBackfillOpen(true);
    setBackfillLoading(true);
    try {
      const r = await api.backfillMasterCartons(true); // dry-run preview only
      setBackfillPreview(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview backfill');
      setBackfillOpen(false);
    } finally { setBackfillLoading(false); }
  };

  const runBackfill = async () => {
    setBackfillApplying(true);
    try {
      const r = await api.backfillMasterCartons(false); // apply
      toast.success(`Backfill done — ${r.slips_needing_fix} slip(s) fixed, ${r.cartons_closed + r.cartons_created} carton(s) finalised.`);
      setBackfillOpen(false);
      setBackfillPreview(null);
      load({ page: 1 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally { setBackfillApplying(false); }
  };

  const doSearch = () => { setPage(1); load({ page: 1 }); };
  const clearAll = () => { setQ(''); setDateFrom(''); setDateTo(''); setPage(1); load({ q: '', dateFrom: '', dateTo: '', page: 1 }); };

  const printSlip = async (mcId, mcNo) => {
    setPrinting(mcId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(api.masterCartonSlipUrl(mcId), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // popup blocked — fall back to download
        const a = document.createElement('a');
        a.href = url; a.download = `master_carton_${String(mcNo || mcId).replace(/\//g, '_')}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Failed to open master carton slip'); }
    finally { setPrinting(''); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goPage = (p) => { const np = Math.min(Math.max(1, p), totalPages); setPage(np); load({ page: np }); };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-master-carton-history-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="mch-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Boxes className="w-6 h-6 mr-3 text-rose-400" />
          <div className="flex-1">
            <h1 className="text-xl font-bold font-display">Master Carton History</h1>
            <p className="text-sm text-[#E8DCC8]">Reprint packed master-carton slips (customer, QR &amp; contents)</p>
          </div>
          <Button variant="ghost" onClick={() => load()} className="text-[#F9FAFB] hover:bg-white/10" data-testid="mch-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {canManage && (
            <Button variant="outline" onClick={openBackfill}
              className="ml-2 bg-transparent border-rose-400/50 text-rose-200 hover:bg-rose-500/10" data-testid="mch-backfill-btn">
              <Wrench className="w-4 h-4 mr-2" />Backfill picked boxes
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-[#5A5F6E]">Search (carton no / customer / DC / transporter / box)</label>
            <div className="flex gap-2 mt-1">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
                placeholder="Search…" className="flex-1" data-testid="mch-search-input" />
              <Button onClick={doSearch} className="bg-rose-600 hover:bg-rose-700" data-testid="mch-search-btn"><Search className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#5A5F6E]">Packed from</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="mch-date-from" />
          </div>
          <div>
            <label className="text-xs text-[#5A5F6E]">to</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="mch-date-to" />
          </div>
          <Button variant="outline" onClick={doSearch} data-testid="mch-apply-dates-btn">Apply</Button>
          <Button variant="ghost" onClick={clearAll} className="text-[#5A5F6E]" data-testid="mch-clear-btn"><X className="w-4 h-4 mr-1" />Clear</Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 text-center" data-testid="mch-summary-cartons">
            <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_cartons}</p>
            <p className="text-xs text-[#5A5F6E] mt-1">Master Cartons</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 text-center" data-testid="mch-summary-boxes">
            <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_boxes}</p>
            <p className="text-xs text-[#5A5F6E] mt-1">Small Boxes</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E5DFD0] p-4 text-center" data-testid="mch-summary-qty">
            <p className="text-2xl font-bold text-[#1A1F2E]">{summary.total_qty}</p>
            <p className="text-xs text-[#5A5F6E] mt-1">Total Pieces</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <p className="text-center text-[#5A5F6E] py-16"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-[#5A5F6E] py-16" data-testid="mch-empty">No closed master cartons found.</p>
          ) : (
            <Table data-testid="mch-table">
              <TableHeader>
                <TableRow className="bg-[#1A1F2E] hover:bg-[#1A1F2E]">
                  <TableHead className="text-white">Carton No</TableHead>
                  <TableHead className="text-white">Customer</TableHead>
                  <TableHead className="text-white">DC No</TableHead>
                  <TableHead className="text-white">Transporter</TableHead>
                  <TableHead className="text-white">Status</TableHead>
                  <TableHead className="text-white text-right">Boxes</TableHead>
                  <TableHead className="text-white text-right">Qty</TableHead>
                  <TableHead className="text-white">Packed At (IST)</TableHead>
                  <TableHead className="text-white text-right">Slip</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.mc_id} data-testid={`mch-row-${c.mc_id}`}>
                    <TableCell className="font-mono font-semibold">{c.master_carton_no}</TableCell>
                    <TableCell>{c.customer || '—'}</TableCell>
                    <TableCell className="font-mono">{c.dc_number || '—'}</TableCell>
                    <TableCell>{c.transport_name || '—'}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-right">{c.total_boxes}</TableCell>
                    <TableCell className="text-right">{c.total_qty}</TableCell>
                    <TableCell className="text-sm">{fmtIst(c.closed_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => printSlip(c.mc_id, c.master_carton_no)} disabled={printing === c.mc_id}
                        className="bg-rose-600 hover:bg-rose-700" data-testid={`mch-print-${c.mc_id}`}>
                        {printing === c.mc_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-1" />Print</>}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="mch-prev-btn">Prev</Button>
            <span className="text-sm text-[#5A5F6E]" data-testid="mch-page-info">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="mch-next-btn">Next</Button>
          </div>
        )}
      </main>

      {/* Admin: backfill picked boxes into finalised master cartons */}
      <Dialog open={backfillOpen} onOpenChange={(o) => { if (!backfillApplying) setBackfillOpen(o); }}>
        <DialogContent data-testid="mch-backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-rose-600" /> Backfill picked boxes
            </DialogTitle>
            <DialogDescription>
              Finalises master cartons for past completed / dispatched pick slips whose picked
              boxes were never closed into a carton, so they appear here for reprint.
            </DialogDescription>
          </DialogHeader>

          {backfillLoading ? (
            <p className="text-center text-[#5A5F6E] py-8" data-testid="mch-backfill-loading">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Scanning slips…
            </p>
          ) : backfillPreview ? (
            <div className="space-y-3" data-testid="mch-backfill-preview">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-[#E5DFD0] p-3 text-center">
                  <p className="text-2xl font-bold text-[#1A1F2E]">{backfillPreview.slips_needing_fix}</p>
                  <p className="text-xs text-[#5A5F6E]">Slips to fix</p>
                </div>
                <div className="rounded-lg border border-[#E5DFD0] p-3 text-center">
                  <p className="text-2xl font-bold text-[#1A1F2E]">{(backfillPreview.cartons_closed || 0) + (backfillPreview.cartons_created || 0)}</p>
                  <p className="text-xs text-[#5A5F6E]">Cartons to finalise</p>
                </div>
                <div className="rounded-lg border border-[#E5DFD0] p-3 text-center">
                  <p className="text-2xl font-bold text-[#1A1F2E]">{backfillPreview.boxes_added}</p>
                  <p className="text-xs text-[#5A5F6E]">Boxes to add</p>
                </div>
                <div className="rounded-lg border border-[#E5DFD0] p-3 text-center">
                  <p className="text-2xl font-bold text-[#1A1F2E]">{backfillPreview.slips_scanned}</p>
                  <p className="text-xs text-[#5A5F6E]">Slips scanned</p>
                </div>
              </div>
              {backfillPreview.slips_needing_fix === 0 ? (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="mch-backfill-nochange">
                  Nothing to fix — all completed / dispatched slips already have their boxes in finalised master cartons.
                </p>
              ) : (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  This is a read-only preview. Nothing has changed yet — press “Run backfill” to apply.
                </p>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setBackfillOpen(false)} disabled={backfillApplying} data-testid="mch-backfill-cancel">Cancel</Button>
            <Button onClick={runBackfill}
              disabled={backfillApplying || backfillLoading || !backfillPreview || backfillPreview.slips_needing_fix === 0}
              className="bg-rose-600 hover:bg-rose-700" data-testid="mch-backfill-run">
              {backfillApplying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Running…</> : 'Run backfill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalMasterCartonHistory;
